import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { CSVUploadModal } from '../components/CSVUploadModal';
import { getSettings, updateSettings, getUsers, updateUser, createUser, deleteUser, testEmailIntegration, sendEscalationDigest, sendDailyDigest, previewDailyDigest, sendWeeklyReport, previewWeeklyReport, resetDatabase, getActivityLogs, exportAllDealsCsv, testNightlyExport, getNightlyExportStatus, uploadLogo, deleteLogo } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Save, Users, Settings as SettingsIcon, Bell, Clock, FileText, Plus, X, Loader2, Mail, Send, Calendar, Eye, Upload, Trash2, AlertTriangle, Search, Download, Activity, Edit2, UserPlus, Shield, BarChart3, ExternalLink, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';

export const SettingsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newStip, setNewStip] = useState('');
  const [testingEmail, setTestingEmail] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [sendingDailyDigest, setSendingDailyDigest] = useState(false);
  const [digestPreview, setDigestPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sendingWeeklyReport, setSendingWeeklyReport] = useState(false);
  const [weeklyPreview, setWeeklyPreview] = useState(null);
  const [loadingWeeklyPreview, setLoadingWeeklyPreview] = useState(false);
  const [showCSVUpload, setShowCSVUpload] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [testingNightlyExport, setTestingNightlyExport] = useState(false);
  const [nightlyStatus, setNightlyStatus] = useState(null);
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [resetting, setResetting] = useState(false);
  const [newLender, setNewLender] = useState('');
  // User creation
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'staff' });
  const [creatingUser, setCreatingUser] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPassword, setEditPassword] = useState('');
  // Activity log
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityFilters, setActivityFilters] = useState({ user_id: '', category: '', search: '', date_from: '', date_to: '' });
  const [expandedLogId, setExpandedLogId] = useState(null);
  // Logo / branding
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'general_manager';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [settingsRes, usersRes] = await Promise.all([
        getSettings(),
        isAdmin ? getUsers() : Promise.resolve({ data: [] }),
      ]);
      
      setSettings(settingsRes.data);
      if (settingsRes.data?.logo_url) setLogoUrl(settingsRes.data.logo_url);
      if (isAdmin) loadNightlyStatus();
      setUsers(usersRes.data || []);
    } catch (err) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await updateSettings(settings);
      toast.success('Settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAddStip = () => {
    if (!newStip.trim()) return;
    if (settings.required_stipulations.includes(newStip.trim())) {
      toast.error('Stipulation already exists');
      return;
    }
    setSettings({
      ...settings,
      required_stipulations: [...settings.required_stipulations, newStip.trim()],
    });
    setNewStip('');
  };

  const handleRemoveStip = (stip) => {
    setSettings({
      ...settings,
      required_stipulations: settings.required_stipulations.filter(s => s !== stip),
    });
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      await updateUser(userId, updates);
      toast.success('User updated');
      setEditingUserId(null);
      loadData();
    } catch (err) {
      toast.error('Failed to update user');
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('All fields are required');
      return;
    }
    setCreatingUser(true);
    try {
      await createUser(newUser);
      toast.success('User created');
      setShowAddUser(false);
      setNewUser({ name: '', email: '', password: '', role: 'staff' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreatingUser(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}? This cannot be undone.`)) return;
    try {
      await deleteUser(userId);
      toast.success('User deleted');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const loadActivityLogs = async (filters = activityFilters) => {
    setActivityLoading(true);
    try {
      const params = {};
      if (filters.user_id) params.user_id = filters.user_id;
      if (filters.category) params.category = filters.category;
      if (filters.search) params.search = filters.search;
      if (filters.date_from) params.date_from = new Date(filters.date_from).toISOString();
      if (filters.date_to) params.date_to = new Date(filters.date_to + 'T23:59:59').toISOString();
      const res = await getActivityLogs(params);
      setActivityLogs(res.data || []);
    } catch (err) {
      toast.error('Failed to load activity logs');
    } finally {
      setActivityLoading(false);
    }
  };

  const handleExportLogs = () => {
    const params = new URLSearchParams();
    if (activityFilters.user_id) params.set('user_id', activityFilters.user_id);
    if (activityFilters.category) params.set('category', activityFilters.category);
    if (activityFilters.search) params.set('search', activityFilters.search);
    if (activityFilters.date_from) params.set('date_from', new Date(activityFilters.date_from).toISOString());
    if (activityFilters.date_to) params.set('date_to', new Date(activityFilters.date_to + 'T23:59:59').toISOString());
    const token = localStorage.getItem('token');
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    window.open(`${backendUrl}/api/activity/export?${params.toString()}&format=csv`, '_blank');
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + 
      ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const actionLabels = {
    user_login: 'Login', user_registered: 'Registered', user_created: 'User Created', user_updated: 'User Updated', user_deleted: 'User Deleted',
    deal_created: 'Deal Created', deal_updated: 'Deal Updated', deal_deleted: 'Deal Deleted',
    deal_funded: 'Deal Funded', deal_unfunded: 'Deal Unfunded', deal_archived: 'Deal Archived', deal_unarchived: 'Deal Restored',
    stip_uploaded: 'Stip Uploaded', stip_verified: 'Stip Verified', stip_added: 'Stip Added', stip_removed: 'Stip Removed',
    stips_bulk_updated: 'Stips Updated', stip_renamed: 'Stip Renamed', stip_file_uploaded: 'Stip File Uploaded',
    note_created: 'Note Added', note_updated: 'Note Updated', note_deleted: 'Note Deleted',
    interview_scheduled: 'Interview Scheduled', interview_rescheduled: 'Interview Rescheduled',
    interview_completed: 'Interview Completed', interview_reminder_manual: 'Reminder Sent',
    settings_updated: 'Settings Updated', escalation_created: 'Escalation',
    csv_imported: 'CSV Import', orphan_cleanup: 'Orphan Cleanup',
    customer_document_uploaded: 'Customer Upload', document_verified: 'Doc Verified', document_rejected: 'Doc Rejected',
    upload_link_sent: 'Upload Link Sent', manual_test_export: 'Test Export',
    escalation_digest_sent: 'Digest Sent', daily_digest_sent: 'Daily Digest',
    nightly_export_started: 'Export Started', nightly_export_csv_generated: 'Export Generated',
    nightly_export_email_sent: 'Export Emailed', email_test: 'Email Test', sms_test: 'SMS Test',
  };

  const formatFieldName = (field) => {
    return field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const renderChangeValue = (val) => {
    if (val === null || val === undefined) return <span className="text-slate-400 italic">empty</span>;
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (typeof val === 'number') return val.toLocaleString();
    if (Array.isArray(val)) return val.join(', ');
    return String(val);
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      const res = await testEmailIntegration();
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message || 'Email test failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleSendDigest = async () => {
    setSendingDigest(true);
    try {
      const res = await sendEscalationDigest();
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message || 'Failed to send digest');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send escalation digest');
    } finally {
      setSendingDigest(false);
    }
  };

  const handleSendDailyDigest = async () => {
    setSendingDailyDigest(true);
    try {
      const res = await sendDailyDigest();
      if (res.data.success) {
        toast.success(`${res.data.message} (${res.data.deals_count} deals, $${res.data.cash_at_risk?.toLocaleString() || 0} at risk)`);
      } else {
        toast.error(res.data.message || 'Failed to send daily digest');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send daily digest');
    } finally {
      setSendingDailyDigest(false);
    }
  };

  const handlePreviewDigest = async () => {
    setLoadingPreview(true);
    try {
      const res = await previewDailyDigest();
      setDigestPreview(res.data);
    } catch (err) {
      toast.error('Failed to load digest preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSendWeeklyReport = async () => {
    setSendingWeeklyReport(true);
    try {
      const res = await sendWeeklyReport();
      if (res.data.success) {
        toast.success(`${res.data.message} (${res.data.deals_funded_count} funded, $${res.data.total_funded_amount?.toLocaleString() || 0})`);
      } else {
        toast.error(res.data.message || 'Failed to send weekly report');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send weekly report');
    } finally {
      setSendingWeeklyReport(false);
    }
  };

  const handlePreviewWeeklyReport = async () => {
    setLoadingWeeklyPreview(true);
    try {
      const res = await previewWeeklyReport();
      setWeeklyPreview(res.data);
    } catch (err) {
      toast.error('Failed to load weekly report preview');
    } finally {
      setLoadingWeeklyPreview(false);
    }
  };

  const handleResetDatabase = async () => {
    if (resetConfirmText !== 'RESET') return;
    setResetting(true);
    try {
      const res = await resetDatabase();
      toast.success(res.data.message);
      setResetConfirmText('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset database');
    } finally {
      setResetting(false);
    }
  };

  const handleExportCsv = async () => {
    setExportingCsv(true);
    try {
      const res = await exportAllDealsCsv();
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dealer_wingman_deals_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to export CSV');
    } finally {
      setExportingCsv(false);
    }
  };

  const handleTestNightlyExport = async () => {
    setTestingNightlyExport(true);
    try {
      const res = await testNightlyExport();
      if (res.data.success) {
        toast.success(`${res.data.message} (${res.data.deals} deals, file: ${res.data.filename})`);
      } else {
        toast.error(res.data.message || 'Test export failed');
      }
      loadNightlyStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test export failed');
    } finally {
      setTestingNightlyExport(false);
    }
  };

  const loadNightlyStatus = async () => {
    try {
      const res = await getNightlyExportStatus();
      setNightlyStatus(res.data);
    } catch (err) { /* ignore */ }
  };

  if (loading) {
    return (
      <Layout>
        <Header title="Settings" subtitle="Configure system settings" />
        <div className="p-6 flex items-center justify-center">
          <Loader2 size={32} className="animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Header title="Settings" subtitle="Configure system settings" />

      <div className="p-6" data-testid="settings-page">
        <Tabs defaultValue="general" className="space-y-6">
          <TabsList className="bg-slate-100 p-1">
            <TabsTrigger value="general" className="data-[state=active]:bg-white">
              <SettingsIcon size={16} className="mr-2" />
              General
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="branding" className="data-[state=active]:bg-white">
                <Edit2 size={16} className="mr-2" />
                Branding
              </TabsTrigger>
            )}
            <TabsTrigger value="stipulations" className="data-[state=active]:bg-white">
              <FileText size={16} className="mr-2" />
              Stipulations
            </TabsTrigger>
            <TabsTrigger value="thresholds" className="data-[state=active]:bg-white">
              <Clock size={16} className="mr-2" />
              Thresholds
            </TabsTrigger>
            <TabsTrigger value="lenders" className="data-[state=active]:bg-white">
              <BarChart3 size={16} className="mr-2" />
              Lenders
            </TabsTrigger>
            <TabsTrigger value="notifications" className="data-[state=active]:bg-white">
              <Bell size={16} className="mr-2" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="data-import" className="data-[state=active]:bg-white">
              <Upload size={16} className="mr-2" />
              Data
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="users" className="data-[state=active]:bg-white">
                <Users size={16} className="mr-2" />
                Users
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="activity" className="data-[state=active]:bg-white" onClick={() => { if (activityLogs.length === 0) loadActivityLogs(); }}>
                <Activity size={16} className="mr-2" />
                Activity Log
              </TabsTrigger>
            )}
          </TabsList>

          {/* General Settings */}
          <TabsContent value="general" className="space-y-4">
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-900 mb-4">
                General Settings
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200">
                  <div>
                    <p className="font-medium text-slate-900">Email Notifications</p>
                    <p className="text-sm text-slate-500">Send email alerts for critical events</p>
                  </div>
                  <Switch
                    checked={settings?.notification_email_enabled}
                    onCheckedChange={(checked) => setSettings({ ...settings, notification_email_enabled: checked })}
                    data-testid="toggle-email"
                  />
                </div>
                
                <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200">
                  <div>
                    <p className="font-medium text-slate-900">In-App Notifications</p>
                    <p className="text-sm text-slate-500">Show notifications in the dashboard</p>
                  </div>
                  <Switch
                    checked={settings?.notification_in_app_enabled}
                    onCheckedChange={(checked) => setSettings({ ...settings, notification_in_app_enabled: checked })}
                    data-testid="toggle-inapp"
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Branding */}
          {isAdmin && (
            <TabsContent value="branding" className="space-y-4">
              <div className="bg-white border border-slate-200 p-6">
                <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-900 mb-4">
                  Dealership Logo
                </h3>
                <p className="text-sm text-slate-500 mb-6">
                  Upload your dealership logo. It will replace the "Dealer Wingman" text in the sidebar. Recommended: PNG or SVG with transparent background, max 2MB.
                </p>

                {/* Current Logo Preview */}
                {logoUrl && (
                  <div className="mb-6 p-4 border border-slate-200 rounded bg-slate-50">
                    <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-3">Current Logo</p>
                    <div className="flex items-center gap-4">
                      <div className="bg-black rounded p-4 inline-flex">
                        <img
                          src={logoUrl}
                          alt="Dealership Logo"
                          className="max-h-14 w-auto object-contain"
                          data-testid="branding-logo-preview"
                        />
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={async () => {
                          try {
                            await deleteLogo();
                            setLogoUrl(null);
                            window.dispatchEvent(new CustomEvent('logo-updated', { detail: null }));
                            toast.success('Logo removed');
                          } catch (err) {
                            toast.error('Failed to remove logo');
                          }
                        }}
                        data-testid="remove-logo-btn"
                      >
                        <Trash2 size={14} className="mr-1" /> Remove Logo
                      </Button>
                    </div>
                  </div>
                )}

                {/* Upload */}
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                    {logoUrl ? 'Replace Logo' : 'Upload Logo'}
                  </Label>
                  <div className="mt-2 flex items-center gap-3">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml"
                      id="logo-upload-input"
                      className="hidden"
                      data-testid="logo-upload-input"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 2 * 1024 * 1024) {
                          toast.error('File too large. Max 2MB.');
                          return;
                        }
                        setUploadingLogo(true);
                        try {
                          const res = await uploadLogo(file);
                          const newUrl = res.data.logo_url;
                          setLogoUrl(newUrl);
                          window.dispatchEvent(new CustomEvent('logo-updated', { detail: newUrl }));
                          toast.success('Logo uploaded successfully');
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Failed to upload logo');
                        } finally {
                          setUploadingLogo(false);
                          e.target.value = '';
                        }
                      }}
                    />
                    <Button
                      variant="outline"
                      onClick={() => document.getElementById('logo-upload-input').click()}
                      disabled={uploadingLogo}
                      data-testid="upload-logo-btn"
                    >
                      {uploadingLogo ? (
                        <><Loader2 size={14} className="mr-2 animate-spin" /> Uploading...</>
                      ) : (
                        <><Upload size={14} className="mr-2" /> Choose File</>
                      )}
                    </Button>
                    <span className="text-xs text-slate-400">PNG, JPEG, WEBP, or SVG — max 2MB</span>
                  </div>
                </div>
              </div>
            </TabsContent>
          )}

          {/* Stipulations */}
          <TabsContent value="stipulations" className="space-y-4">
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-900 mb-4">
                Required Stipulations
              </h3>
              <p className="text-sm text-slate-500 mb-4">
                Configure the 10 required documents for each deal
              </p>
              
              <div className="space-y-3 mb-6">
                {settings?.required_stipulations?.map((stip, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200"
                    data-testid={`stip-setting-${idx}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-slate-200 text-slate-600 flex items-center justify-center text-sm font-mono">
                        {idx + 1}
                      </span>
                      <span className="text-sm font-medium text-slate-900">{stip}</span>
                    </div>
                    {isAdmin && (
                      <button
                        onClick={() => handleRemoveStip(stip)}
                        className="text-slate-400 hover:text-red-600"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {isAdmin && (
                <div className="flex gap-3">
                  <Input
                    value={newStip}
                    onChange={(e) => setNewStip(e.target.value)}
                    placeholder="New stipulation name..."
                    onKeyDown={(e) => e.key === 'Enter' && handleAddStip()}
                    data-testid="new-stip-input"
                  />
                  <Button onClick={handleAddStip} data-testid="add-stip-btn">
                    <Plus size={16} className="mr-2" />
                    Add
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Thresholds */}
          <TabsContent value="thresholds" className="space-y-4">
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-900 mb-4">
                Deal Aging Thresholds
              </h3>
              <p className="text-sm text-slate-500 mb-4">
                Configure when deals escalate based on age
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                    Warning (Yellow) - Days
                  </Label>
                  <Input
                    type="number"
                    value={settings?.day_warning_threshold || 3}
                    onChange={(e) => setSettings({ ...settings, day_warning_threshold: parseInt(e.target.value) })}
                    className="mt-1"
                    disabled={!isAdmin}
                    data-testid="threshold-warning"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                    Critical (Red) - Days
                  </Label>
                  <Input
                    type="number"
                    value={settings?.day_critical_threshold || 4}
                    onChange={(e) => setSettings({ ...settings, day_critical_threshold: parseInt(e.target.value) })}
                    className="mt-1"
                    disabled={!isAdmin}
                    data-testid="threshold-critical"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                    Urgent (Escalate) - Days
                  </Label>
                  <Input
                    type="number"
                    value={settings?.day_urgent_threshold || 5}
                    onChange={(e) => setSettings({ ...settings, day_urgent_threshold: parseInt(e.target.value) })}
                    className="mt-1"
                    disabled={!isAdmin}
                    data-testid="threshold-urgent"
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Lenders */}
          <TabsContent value="lenders" className="space-y-4">
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-900 mb-4">
                Lender Management
              </h3>
              <p className="text-sm text-slate-500 mb-4">
                Add lenders that will appear in the deal form dropdown
              </p>

              <div className="flex gap-2 mb-6">
                <Input
                  value={newLender}
                  onChange={(e) => setNewLender(e.target.value.toUpperCase())}
                  placeholder="Enter lender name"
                  className="flex-1"
                  data-testid="new-lender-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newLender.trim()) {
                      const name = newLender.trim();
                      const current = settings?.lenders_list || [];
                      if (!current.some(l => l.toUpperCase() === name.toUpperCase())) {
                        const updated = [...current, name].sort();
                        setSettings({ ...settings, lenders_list: updated });
                        updateSettings({ lenders_list: updated }).then(() => toast.success('Lender added')).catch(() => toast.error('Failed to save'));
                      } else {
                        toast.error('Lender already exists');
                      }
                      setNewLender('');
                    }
                  }}
                />
                <Button
                  onClick={() => {
                    const name = newLender.trim();
                    if (!name) return;
                    const current = settings?.lenders_list || [];
                    if (!current.some(l => l.toUpperCase() === name.toUpperCase())) {
                      const updated = [...current, name].sort();
                      setSettings({ ...settings, lenders_list: updated });
                      updateSettings({ lenders_list: updated }).then(() => toast.success('Lender added')).catch(() => toast.error('Failed to save'));
                    } else {
                      toast.error('Lender already exists');
                    }
                    setNewLender('');
                  }}
                  disabled={!newLender.trim()}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="add-lender-btn"
                >
                  <Plus size={16} className="mr-1" /> Add
                </Button>
              </div>

              <div className="space-y-2">
                {(settings?.lenders_list || []).length === 0 && (
                  <p className="text-sm text-slate-400 italic py-4 text-center">No lenders configured. Add your first lender above.</p>
                )}
                {(settings?.lenders_list || []).map((lender, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200">
                    <span className="font-medium text-slate-900" data-testid={`lender-name-${idx}`}>{lender}</span>
                    <button
                      onClick={() => {
                        const updated = (settings?.lenders_list || []).filter((_, i) => i !== idx);
                        setSettings({ ...settings, lenders_list: updated });
                        updateSettings({ lenders_list: updated }).then(() => toast.success('Lender removed')).catch(() => toast.error('Failed to save'));
                      }}
                      className="text-red-500 hover:text-red-700 p-1"
                      data-testid={`remove-lender-${idx}`}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Notifications */}
          <TabsContent value="notifications" className="space-y-4">
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-900 mb-4">
                Notification Channels
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200">
                  <div>
                    <p className="font-medium text-slate-900">Email Notifications</p>
                    <p className="text-sm text-slate-500">Receive alerts via email (SendGrid)</p>
                  </div>
                  <Switch
                    checked={settings?.notification_email_enabled}
                    onCheckedChange={(checked) => setSettings({ ...settings, notification_email_enabled: checked })}
                    disabled={!isAdmin}
                  />
                </div>
                
                <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200">
                  <div>
                    <p className="font-medium text-slate-900">In-App Notifications</p>
                    <p className="text-sm text-slate-500">Show alerts in the dashboard bell icon</p>
                  </div>
                  <Switch
                    checked={settings?.notification_in_app_enabled}
                    onCheckedChange={(checked) => setSettings({ ...settings, notification_in_app_enabled: checked })}
                    disabled={!isAdmin}
                  />
                </div>
              </div>
            </div>

            {/* Email Testing Section - Admin/GM Only */}
            {isAdmin && (
              <div className="bg-white border border-slate-200 p-6">
                <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-900 mb-4">
                  Email Integration
                </h3>
                <p className="text-sm text-slate-500 mb-4">
                  Test your SendGrid email integration or send escalation digests to management
                </p>
                
                <div className="flex gap-3">
                  <Button
                    onClick={handleTestEmail}
                    disabled={testingEmail}
                    variant="outline"
                    className="uppercase tracking-wide text-xs font-semibold"
                    data-testid="test-email-btn"
                  >
                    {testingEmail ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail size={16} className="mr-2" />
                        Test Email
                      </>
                    )}
                  </Button>
                  
                  <Button
                    onClick={handleSendDigest}
                    disabled={sendingDigest}
                    variant="outline"
                    className="uppercase tracking-wide text-xs font-semibold"
                    data-testid="send-digest-btn"
                  >
                    {sendingDigest ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={16} className="mr-2" />
                        Send Escalation Digest
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Daily Digest Section - Admin/GM Only */}
            {isAdmin && (
              <div className="bg-white border border-slate-200 p-6">
                <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-900 mb-4">
                  Daily Digest
                </h3>
                <p className="text-sm text-slate-500 mb-4">
                  Automated daily email summarizing all deals aging 3+ days. Sent every morning at 7 AM UTC.
                </p>
                
                <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 mb-4">
                  <div>
                    <p className="font-medium text-slate-900">Enable Daily Digest</p>
                    <p className="text-sm text-slate-500">Send automated morning summary to all managers</p>
                  </div>
                  <Switch
                    checked={settings?.daily_digest_enabled}
                    onCheckedChange={(checked) => setSettings({ ...settings, daily_digest_enabled: checked })}
                    data-testid="toggle-daily-digest"
                  />
                </div>
                
                <div className="flex gap-3 mb-4">
                  <Button
                    onClick={handlePreviewDigest}
                    disabled={loadingPreview}
                    variant="outline"
                    className="uppercase tracking-wide text-xs font-semibold"
                    data-testid="preview-digest-btn"
                  >
                    {loadingPreview ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <Eye size={16} className="mr-2" />
                        Preview Digest
                      </>
                    )}
                  </Button>
                  
                  <Button
                    onClick={handleSendDailyDigest}
                    disabled={sendingDailyDigest}
                    variant="outline"
                    className="uppercase tracking-wide text-xs font-semibold"
                    data-testid="send-daily-digest-btn"
                  >
                    {sendingDailyDigest ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Calendar size={16} className="mr-2" />
                        Send Now
                      </>
                    )}
                  </Button>
                </div>
                
                {/* Digest Preview */}
                {digestPreview && (
                  <div className="bg-slate-50 border border-slate-200 p-4 mt-4">
                    <h4 className="font-medium text-slate-900 mb-3">Digest Preview</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                      <div className="bg-white p-3 text-center border border-slate-200">
                        <div className="text-2xl font-bold text-red-600">{digestPreview.deals_count}</div>
                        <div className="text-xs text-slate-500 uppercase">Aging Deals</div>
                      </div>
                      <div className="bg-white p-3 text-center border border-slate-200">
                        <div className="text-2xl font-bold text-amber-600">${digestPreview.cash_at_risk?.toLocaleString() || 0}</div>
                        <div className="text-xs text-slate-500 uppercase">Cash at Risk</div>
                      </div>
                      <div className="bg-white p-3 text-center border border-slate-200">
                        <div className="text-2xl font-bold text-red-700">{digestPreview.critical_count}</div>
                        <div className="text-xs text-slate-500 uppercase">Critical (5+ days)</div>
                      </div>
                      <div className="bg-white p-3 text-center border border-slate-200">
                        <div className="text-2xl font-bold text-slate-600">{digestPreview.recipient_count}</div>
                        <div className="text-xs text-slate-500 uppercase">Recipients</div>
                      </div>
                    </div>
                    
                    {digestPreview.deals?.length > 0 && (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-slate-100">
                              <th className="px-3 py-2 text-left text-xs uppercase text-slate-500">Deal #</th>
                              <th className="px-3 py-2 text-left text-xs uppercase text-slate-500">Customer</th>
                              <th className="px-3 py-2 text-left text-xs uppercase text-slate-500">Age</th>
                              <th className="px-3 py-2 text-right text-xs uppercase text-slate-500">Amount</th>
                              <th className="px-3 py-2 text-center text-xs uppercase text-slate-500">Stips</th>
                            </tr>
                          </thead>
                          <tbody>
                            {digestPreview.deals.map((deal, idx) => (
                              <tr key={idx} className="border-b border-slate-200">
                                <td className="px-3 py-2 font-medium">{deal.deal_number}</td>
                                <td className="px-3 py-2">{deal.customer_name}</td>
                                <td className={`px-3 py-2 font-semibold ${deal.deal_age >= 5 ? 'text-red-600' : 'text-amber-600'}`}>
                                  {deal.deal_age} days
                                </td>
                                <td className="px-3 py-2 text-right">${deal.pending_funds?.toLocaleString() || 0}</td>
                                <td className="px-3 py-2 text-center">{deal.missing_stips} missing</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    
                    {digestPreview.deals_count === 0 && (
                      <p className="text-slate-500 text-center py-4">No aging deals (3+ days) found</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Weekly Performance Report Section - Admin/GM Only */}
            {isAdmin && (
              <div className="bg-white border border-slate-200 p-6" data-testid="weekly-report-section">
                <div className="flex items-center gap-2 mb-1">
                  <BarChart3 size={20} className="text-emerald-600" />
                  <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-900">
                    Weekly Performance Report
                  </h3>
                </div>
                <p className="text-sm text-slate-500 mb-4">
                  Automated weekly email summarizing deals funded, average days-to-fund, top managers, top lenders, and pipeline health. Sent to Admins &amp; GMs.
                </p>
                
                <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 mb-4">
                  <div>
                    <p className="font-medium text-slate-900">Enable Weekly Report</p>
                    <p className="text-sm text-slate-500">Send automated performance summary to management</p>
                  </div>
                  <Switch
                    checked={settings?.weekly_report_enabled ?? true}
                    onCheckedChange={(checked) => setSettings({ ...settings, weekly_report_enabled: checked })}
                    data-testid="toggle-weekly-report"
                  />
                </div>

                {/* Day & Time config */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-500 mb-1 block">Send Day</Label>
                    <select
                      value={settings?.weekly_report_day ?? 4}
                      onChange={(e) => setSettings({ ...settings, weekly_report_day: parseInt(e.target.value) })}
                      className="w-full border border-slate-300 rounded px-3 py-2 text-sm bg-white"
                      data-testid="weekly-report-day-select"
                    >
                      <option value={0}>Monday</option>
                      <option value={1}>Tuesday</option>
                      <option value={2}>Wednesday</option>
                      <option value={3}>Thursday</option>
                      <option value={4}>Friday</option>
                      <option value={5}>Saturday</option>
                      <option value={6}>Sunday</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-500 mb-1 block">Send Time (UTC)</Label>
                    <select
                      value={settings?.weekly_report_hour_utc ?? 17}
                      onChange={(e) => setSettings({ ...settings, weekly_report_hour_utc: parseInt(e.target.value) })}
                      className="w-full border border-slate-300 rounded px-3 py-2 text-sm bg-white"
                      data-testid="weekly-report-hour-select"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>{i.toString().padStart(2, '0')}:00 UTC</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex gap-3 mb-4">
                  <Button
                    onClick={handlePreviewWeeklyReport}
                    disabled={loadingWeeklyPreview}
                    variant="outline"
                    className="uppercase tracking-wide text-xs font-semibold"
                    data-testid="preview-weekly-report-btn"
                  >
                    {loadingWeeklyPreview ? (
                      <><Loader2 size={16} className="mr-2 animate-spin" />Loading...</>
                    ) : (
                      <><Eye size={16} className="mr-2" />Preview Report</>
                    )}
                  </Button>
                  <Button
                    onClick={handleSendWeeklyReport}
                    disabled={sendingWeeklyReport}
                    variant="outline"
                    className="uppercase tracking-wide text-xs font-semibold"
                    data-testid="send-weekly-report-btn"
                  >
                    {sendingWeeklyReport ? (
                      <><Loader2 size={16} className="mr-2 animate-spin" />Sending...</>
                    ) : (
                      <><Send size={16} className="mr-2" />Send Now</>
                    )}
                  </Button>
                </div>

                {/* Weekly Report Preview */}
                {weeklyPreview && (
                  <div className="bg-slate-50 border border-slate-200 p-4 mt-4" data-testid="weekly-report-preview">
                    <h4 className="font-medium text-slate-900 mb-1">Report Preview</h4>
                    <p className="text-xs text-slate-500 mb-3">{weeklyPreview.period_start} — {weeklyPreview.period_end}</p>
                    
                    {/* KPI Row */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                      <div className="bg-white p-3 text-center border border-slate-200">
                        <div className="text-2xl font-bold text-emerald-600">{weeklyPreview.deals_funded_count}</div>
                        <div className="text-xs text-slate-500 uppercase">Deals Funded</div>
                      </div>
                      <div className="bg-white p-3 text-center border border-slate-200">
                        <div className="text-xl font-bold text-emerald-600">${weeklyPreview.total_funded_amount?.toLocaleString() || 0}</div>
                        <div className="text-xs text-slate-500 uppercase">Total Funded</div>
                      </div>
                      <div className="bg-white p-3 text-center border border-slate-200">
                        <div className="text-2xl font-bold text-blue-600">{weeklyPreview.avg_days_to_fund}</div>
                        <div className="text-xs text-slate-500 uppercase">Avg Days to Fund</div>
                      </div>
                      <div className="bg-white p-3 text-center border border-slate-200">
                        <div className="text-2xl font-bold text-amber-600">{weeklyPreview.total_in_transit}</div>
                        <div className="text-xs text-slate-500 uppercase">In Transit</div>
                      </div>
                      <div className="bg-white p-3 text-center border border-slate-200">
                        <div className="text-xl font-bold text-red-600">${weeklyPreview.total_in_transit_amount?.toLocaleString() || 0}</div>
                        <div className="text-xs text-slate-500 uppercase">Cash In Transit</div>
                      </div>
                    </div>

                    {/* Top Managers */}
                    {weeklyPreview.top_managers?.length > 0 && (
                      <div className="mb-4">
                        <h5 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Top Managers</h5>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-slate-100">
                                <th className="px-3 py-2 text-left text-xs uppercase text-slate-500">Manager</th>
                                <th className="px-3 py-2 text-center text-xs uppercase text-slate-500">Deals</th>
                                <th className="px-3 py-2 text-right text-xs uppercase text-slate-500">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {weeklyPreview.top_managers.map((m, idx) => (
                                <tr key={idx} className="border-b border-slate-200">
                                  <td className="px-3 py-2 font-medium">{idx < 3 ? ['\u{1F947}','\u{1F948}','\u{1F949}'][idx] : `#${idx+1}`} {m.name}</td>
                                  <td className="px-3 py-2 text-center font-bold text-emerald-600">{m.deals_funded}</td>
                                  <td className="px-3 py-2 text-right">${m.total_funded?.toLocaleString() || 0}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Top Lenders */}
                    {weeklyPreview.top_lenders?.length > 0 && (
                      <div className="mb-4">
                        <h5 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Top Lenders</h5>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-slate-100">
                                <th className="px-3 py-2 text-left text-xs uppercase text-slate-500">Lender</th>
                                <th className="px-3 py-2 text-center text-xs uppercase text-slate-500">Deals</th>
                                <th className="px-3 py-2 text-right text-xs uppercase text-slate-500">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {weeklyPreview.top_lenders.map((l, idx) => (
                                <tr key={idx} className="border-b border-slate-200">
                                  <td className="px-3 py-2 font-medium">{l.name}</td>
                                  <td className="px-3 py-2 text-center font-bold text-blue-600">{l.deals_funded}</td>
                                  <td className="px-3 py-2 text-right">${l.total_funded?.toLocaleString() || 0}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Aging Breakdown */}
                    <div>
                      <h5 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Pipeline Aging</h5>
                      <div className="grid grid-cols-4 gap-2">
                        <div className="bg-emerald-50 p-2 text-center border border-emerald-200 rounded">
                          <div className="text-lg font-bold text-emerald-600">{weeklyPreview.aging_green}</div>
                          <div className="text-[10px] text-emerald-700 uppercase">&lt;{weeklyPreview.thresholds?.warning || 3}d</div>
                        </div>
                        <div className="bg-amber-50 p-2 text-center border border-amber-200 rounded">
                          <div className="text-lg font-bold text-amber-600">{weeklyPreview.aging_yellow}</div>
                          <div className="text-[10px] text-amber-700 uppercase">{weeklyPreview.thresholds?.warning || 3}-{(weeklyPreview.thresholds?.critical || 4)-1}d</div>
                        </div>
                        <div className="bg-red-50 p-2 text-center border border-red-200 rounded">
                          <div className="text-lg font-bold text-red-600">{weeklyPreview.aging_red}</div>
                          <div className="text-[10px] text-red-700 uppercase">{weeklyPreview.thresholds?.critical || 4}-{(weeklyPreview.thresholds?.urgent || 5)-1}d</div>
                        </div>
                        <div className="bg-red-900 p-2 text-center border border-red-800 rounded">
                          <div className="text-lg font-bold text-red-200">{weeklyPreview.aging_urgent}</div>
                          <div className="text-[10px] text-red-300 uppercase">{weeklyPreview.thresholds?.urgent || 5}+d</div>
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 mt-2 text-center">
                        {weeklyPreview.total_missing_stips} missing stips across {weeklyPreview.total_in_transit} active deals
                        &nbsp;|&nbsp; Report sent to {weeklyPreview.recipient_count} recipient(s)
                      </p>
                    </div>

                    {weeklyPreview.deals_funded_count === 0 && weeklyPreview.total_in_transit === 0 && (
                      <p className="text-slate-500 text-center py-4">No deal data found for this period</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Data Import & Export */}
          <TabsContent value="data-import" className="space-y-4">
            {/* Export Deals */}
            <div className="bg-white border border-slate-200 p-6" data-testid="export-csv-section">
              <div className="flex items-center gap-2 mb-1">
                <Download size={20} className="text-emerald-600" />
                <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-900">
                  Export Deals to CSV
                </h3>
              </div>
              <p className="text-sm text-slate-500 mb-4">
                Download all deals (active in-transit + funded) as a CSV file with full detail columns.
              </p>

              <div className="bg-slate-50 border border-slate-200 p-4 mb-4">
                <h4 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Included Columns</h4>
                <div className="flex flex-wrap gap-2">
                  {['Deal Number', 'Customer Name', 'Vehicle', 'Lender', 'Finance Manager', 'Sales Person', 'Desk Manager', 'Stock Number', 'Amount Financed', 'Cash at Risk', 'Deal Age', 'Deal Date', 'Status', 'Funded Date', 'Days to Fund', 'Stips Total / Verified / Missing', 'Risk Level', 'Customer Phone', 'Customer Email', 'Comments'].map(col => (
                    <span key={col} className="inline-flex items-center px-2 py-1 bg-white border border-slate-200 text-xs text-slate-600 rounded">
                      {col}
                    </span>
                  ))}
                </div>
              </div>

              <Button
                onClick={handleExportCsv}
                disabled={exportingCsv}
                className="uppercase tracking-wide text-xs font-semibold"
                data-testid="export-csv-btn"
              >
                {exportingCsv ? (
                  <><Loader2 size={16} className="mr-2 animate-spin" />Exporting...</>
                ) : (
                  <><Download size={16} className="mr-2" />Export All Deals (CSV)</>
                )}
              </Button>
            </div>

            {/* Automatic Nightly Export */}
            {isAdmin && (
              <div className="bg-white border border-slate-200 p-6" data-testid="nightly-export-section">
                <div className="flex items-center gap-2 mb-1">
                  <Clock size={20} className="text-blue-600" />
                  <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-900">
                    Automatic Nightly Export
                  </h3>
                </div>
                <p className="text-sm text-slate-500 mb-4">
                  Automatically generate and email a CSV export of all deals every night.
                </p>

                <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 mb-4">
                  <div>
                    <p className="font-medium text-slate-900">Enable Automatic Nightly Export</p>
                    <p className="text-sm text-slate-500">Send CSV export to recipient every night</p>
                  </div>
                  <Switch
                    checked={settings?.nightly_export_enabled ?? true}
                    onCheckedChange={(checked) => setSettings({ ...settings, nightly_export_enabled: checked })}
                    data-testid="toggle-nightly-export"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-500 mb-1 block">Export Time (UTC)</Label>
                    <select
                      value={settings?.nightly_export_hour_utc ?? 4}
                      onChange={(e) => setSettings({ ...settings, nightly_export_hour_utc: parseInt(e.target.value) })}
                      className="w-full border border-slate-300 rounded px-3 py-2 text-sm bg-white"
                      data-testid="nightly-export-hour-select"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>{i.toString().padStart(2, '0')}:00 UTC</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-500 mb-1 block">Recipient Email</Label>
                    <Input
                      value={settings?.nightly_export_email ?? 'bwishengrad2@wishautomotive.com'}
                      onChange={(e) => setSettings({ ...settings, nightly_export_email: e.target.value })}
                      placeholder="email@example.com"
                      data-testid="nightly-export-email-input"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3 mb-4">
                  <Button
                    onClick={handleTestNightlyExport}
                    disabled={testingNightlyExport}
                    variant="outline"
                    className="uppercase tracking-wide text-xs font-semibold"
                    data-testid="test-nightly-export-btn"
                  >
                    {testingNightlyExport ? (
                      <><Loader2 size={16} className="mr-2 animate-spin" />Sending...</>
                    ) : (
                      <><Send size={16} className="mr-2" />Test Export</>
                    )}
                  </Button>
                </div>

                {/* Status display */}
                {nightlyStatus && (
                  <div className="bg-slate-50 border border-slate-200 p-4" data-testid="nightly-export-status">
                    <h4 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Export Status</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <div>
                        <p className="text-xs text-slate-500">Status</p>
                        <p className={`text-sm font-semibold ${
                          nightlyStatus.status === 'success' ? 'text-emerald-600' :
                          nightlyStatus.status === 'failed' ? 'text-red-600' : 'text-slate-500'
                        }`}>
                          {nightlyStatus.status === 'success' ? 'Success' :
                           nightlyStatus.status === 'failed' ? 'Failed' : 'Pending'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Last Successful Export</p>
                        <p className="text-sm font-medium text-slate-900">
                          {nightlyStatus.last_success
                            ? new Date(nightlyStatus.last_success).toLocaleString()
                            : 'Never'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Last Failed Export</p>
                        <p className="text-sm font-medium text-slate-900">
                          {nightlyStatus.last_fail
                            ? new Date(nightlyStatus.last_fail).toLocaleString()
                            : 'None'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Import Deals */}
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-900 mb-4">
                Import Deals from CSV
              </h3>
              
              <p className="text-sm text-slate-600 mb-6">
                Upload a CSV file to bulk import deals into the system. The CSV should contain columns for deal number, customer name, vehicle info, lender, amount financed, and other deal details.
              </p>
              
              <div className="bg-slate-50 border border-slate-200 p-6 text-center">
                <Upload size={48} className="mx-auto mb-4 text-slate-400" />
                <h4 className="font-medium text-slate-900 mb-2">Upload CSV File</h4>
                <p className="text-sm text-slate-500 mb-4">
                  Supported format: .csv with headers
                </p>
                <Button
                  onClick={() => setShowCSVUpload(true)}
                  className="uppercase tracking-wide text-xs font-semibold"
                  data-testid="upload-csv-settings-btn"
                >
                  <Upload size={16} className="mr-2" />
                  Select CSV File
                </Button>
              </div>
              
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 text-blue-800 text-sm">
                <h4 className="font-semibold mb-2">Expected CSV Columns:</h4>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li><strong>deal_number</strong> - Unique deal identifier</li>
                  <li><strong>customer_name</strong> - Customer full name</li>
                  <li><strong>vehicle_info</strong> - Year Make Model (e.g., &quot;2024 Honda Accord&quot;)</li>
                  <li><strong>lender</strong> - Financing lender name</li>
                  <li><strong>amount_financed</strong> - Loan amount (numeric)</li>
                  <li><strong>deal_date</strong> - Date of deal (YYYY-MM-DD)</li>
                  <li><strong>finance_manager</strong> - Assigned FM name (optional)</li>
                  <li><strong>customer_phone</strong> - Customer phone (optional)</li>
                  <li><strong>customer_email</strong> - Customer email (optional)</li>
                </ul>
              </div>
            </div>

            {/* Reset Database - Admin Only */}
            {isAdmin && (
              <div className="bg-white border-2 border-red-200 p-6" data-testid="reset-database-section">
                <div className="flex items-start gap-3 mb-4">
                  <AlertTriangle size={24} className="text-red-500 mt-0.5 shrink-0" />
                  <div>
                    <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-red-700">
                      Reset Database
                    </h3>
                    <p className="text-sm text-slate-600 mt-1">
                      Permanently delete all deals, notes, activity logs, notifications, escalations, stipulation files, and customer documents. User accounts will be preserved.
                    </p>
                  </div>
                </div>

                <div className="bg-red-50 border border-red-200 p-4 mb-4">
                  <p className="text-sm text-red-800 font-medium">
                    This action cannot be undone. Type <span className="font-mono bg-red-100 px-1.5 py-0.5 rounded">RESET</span> to confirm.
                  </p>
                  <Input
                    value={resetConfirmText}
                    onChange={(e) => setResetConfirmText(e.target.value)}
                    placeholder="Type RESET to confirm"
                    className="mt-3 max-w-xs border-red-300 focus:border-red-500"
                    data-testid="reset-confirm-input"
                  />
                </div>

                <Button
                  onClick={handleResetDatabase}
                  disabled={resetConfirmText !== 'RESET' || resetting}
                  variant="destructive"
                  className="uppercase tracking-wide text-xs font-semibold"
                  data-testid="reset-database-btn"
                >
                  {resetting ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} className="mr-2" />
                      Reset Database
                    </>
                  )}
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Users */}
          {isAdmin && (
            <TabsContent value="users" className="space-y-4">
              <div className="bg-white border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-900">
                    User Management
                  </h3>
                  <Button size="sm" onClick={() => setShowAddUser(!showAddUser)} data-testid="add-user-btn">
                    <UserPlus size={16} className="mr-2" />
                    Add User
                  </Button>
                </div>

                {/* Add User Form */}
                {showAddUser && (
                  <div className="border border-slate-200 bg-slate-50 p-4 mb-4 space-y-3" data-testid="add-user-form">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Full Name *</Label>
                        <Input placeholder="John Doe" value={newUser.name} onChange={(e) => setNewUser({...newUser, name: e.target.value})} data-testid="new-user-name" />
                      </div>
                      <div>
                        <Label className="text-xs">Email *</Label>
                        <Input type="email" placeholder="user@dealerwingman.com" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} data-testid="new-user-email" />
                      </div>
                      <div>
                        <Label className="text-xs">Temporary Password *</Label>
                        <Input type="password" placeholder="Min 6 characters" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} data-testid="new-user-password" />
                      </div>
                      <div>
                        <Label className="text-xs">Role</Label>
                        <select className="w-full h-9 px-3 border border-slate-300 text-sm bg-white" value={newUser.role} onChange={(e) => setNewUser({...newUser, role: e.target.value})} data-testid="new-user-role">
                          <option value="staff">Sales Person</option>
                          <option value="closer">Closer</option>
                          <option value="finance_manager">Finance Manager</option>
                          <option value="desk_manager">Desk Manager</option>
                          <option value="general_manager">General Manager</option>
                          <option value="admin">Administrator</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" size="sm" onClick={() => setShowAddUser(false)}>Cancel</Button>
                      <Button size="sm" onClick={handleCreateUser} disabled={creatingUser} data-testid="submit-new-user">
                        {creatingUser ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Plus size={14} className="mr-1" />}
                        Create User
                      </Button>
                    </div>
                  </div>
                )}
                
                {users.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No users found</p>
                ) : (
                  <div className="space-y-3">
                    {users.map((u) => (
                      <div
                        key={u.id}
                        className={`flex items-center justify-between p-4 border border-slate-200 ${u.is_active ? 'bg-slate-50' : 'bg-red-50 opacity-70'}`}
                        data-testid={`user-${u.id}`}
                      >
                        <div className="flex-1">
                          {editingUserId === u.id ? (
                            <div className="flex items-center gap-2 flex-wrap">
                              <Input className="w-40 h-8 text-sm" value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Name" data-testid={`edit-name-${u.id}`} />
                              <Input className="w-48 h-8 text-sm" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} placeholder="Email" data-testid={`edit-email-${u.id}`} />
                              <Input className="w-36 h-8 text-sm" type="password" value={editPassword} onChange={(e) => setEditPassword(e.target.value)} placeholder="New password" data-testid={`edit-password-${u.id}`} />
                              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => { const updates = {}; if (editName !== u.name) updates.name = editName; if (editEmail !== u.email) updates.email = editEmail; if (editPassword) updates.password = editPassword; if (Object.keys(updates).length) handleUpdateUser(u.id, updates); else setEditingUserId(null); }} data-testid={`save-user-${u.id}`}>Save</Button>
                              <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setEditingUserId(null)}>Cancel</Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-900">{u.name}</p>
                              {!u.is_active && <span className="text-xs px-2 py-0.5 bg-red-200 text-red-700 font-bold uppercase">Inactive</span>}
                              {u.id !== user?.id && (
                                <button onClick={() => { setEditingUserId(u.id); setEditName(u.name); setEditEmail(u.email); setEditPassword(''); }} className="text-slate-400 hover:text-slate-600">
                                  <Edit2 size={14} />
                                </button>
                              )}
                            </div>
                          )}
                          <p className="text-sm text-slate-500">{u.email}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <select
                            value={u.role}
                            onChange={(e) => handleUpdateUser(u.id, { role: e.target.value })}
                            className="px-3 py-1 border border-slate-300 text-sm"
                            disabled={u.id === user?.id}
                          >
                            <option value="staff">Sales Person</option>
                            <option value="closer">Closer</option>
                            <option value="finance_manager">Finance Manager</option>
                            <option value="desk_manager">Desk Manager</option>
                            <option value="general_manager">General Manager</option>
                            <option value="admin">Administrator</option>
                          </select>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-slate-400">{u.is_active ? 'Active' : 'Inactive'}</span>
                            <Switch
                              checked={u.is_active}
                              onCheckedChange={(checked) => handleUpdateUser(u.id, { is_active: checked })}
                              disabled={u.id === user?.id}
                            />
                          </div>
                          {u.id !== user?.id && (
                            <button onClick={() => handleDeleteUser(u.id, u.name)} className="text-slate-400 hover:text-red-600 p-1" title="Delete user" data-testid={`delete-user-${u.id}`}>
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          )}

          {/* Activity Log */}
          {isAdmin && (
            <TabsContent value="activity" className="space-y-4">
              <div className="bg-white border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-900">
                    User Activity Audit Log
                  </h3>
                  <Button size="sm" variant="outline" onClick={handleExportLogs} data-testid="export-logs-btn">
                    <Download size={16} className="mr-2" />
                    Export CSV
                  </Button>
                </div>

                {/* Filters */}
                <div className="grid grid-cols-5 gap-3 mb-4">
                  <div>
                    <Label className="text-xs">User</Label>
                    <select className="w-full h-9 px-2 border border-slate-300 text-sm bg-white" value={activityFilters.user_id} onChange={(e) => setActivityFilters({...activityFilters, user_id: e.target.value})} data-testid="activity-filter-user">
                      <option value="">All Users</option>
                      {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Category</Label>
                    <select className="w-full h-9 px-2 border border-slate-300 text-sm bg-white" value={activityFilters.category} onChange={(e) => setActivityFilters({...activityFilters, category: e.target.value})} data-testid="activity-filter-category">
                      <option value="">All Categories</option>
                      <option value="auth">Authentication</option>
                      <option value="deal">Deals</option>
                      <option value="stip">Stipulations</option>
                      <option value="lifecycle">Lifecycle</option>
                      <option value="note">Notes</option>
                      <option value="interview">Interviews</option>
                      <option value="admin">Admin</option>
                      <option value="settings">Settings</option>
                      <option value="import">Import</option>
                      <option value="system">System</option>
                      <option value="email">Email</option>
                      <option value="general">General</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">From</Label>
                    <Input type="date" className="h-9" value={activityFilters.date_from} onChange={(e) => setActivityFilters({...activityFilters, date_from: e.target.value})} data-testid="activity-date-from" />
                  </div>
                  <div>
                    <Label className="text-xs">To</Label>
                    <Input type="date" className="h-9" value={activityFilters.date_to} onChange={(e) => setActivityFilters({...activityFilters, date_to: e.target.value})} data-testid="activity-date-to" />
                  </div>
                  <div className="flex items-end gap-2">
                    <Button size="sm" className="h-9" onClick={() => loadActivityLogs()} data-testid="activity-search-btn">
                      <Search size={14} className="mr-1" /> Search
                    </Button>
                  </div>
                </div>

                {/* Search bar */}
                <div className="relative mb-4">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search logs by user, action, deal number, customer..."
                    className="pl-10"
                    value={activityFilters.search}
                    onChange={(e) => setActivityFilters({...activityFilters, search: e.target.value})}
                    onKeyDown={(e) => { if (e.key === 'Enter') loadActivityLogs(); }}
                    data-testid="activity-search-input"
                  />
                </div>

                {/* Log Table */}
                {activityLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="animate-spin" /></div>
                ) : activityLogs.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No activity logs found. Click Search to load.</p>
                ) : (
                  <div className="overflow-auto max-h-[600px]" data-testid="activity-log-container">
                    <table className="w-full text-sm" data-testid="activity-log-table">
                      <thead className="bg-slate-100 sticky top-0 z-10">
                        <tr>
                          <th className="text-left p-2 text-xs uppercase tracking-wider text-slate-500 font-semibold w-[140px]">Timestamp</th>
                          <th className="text-left p-2 text-xs uppercase tracking-wider text-slate-500 font-semibold w-[120px]">User</th>
                          <th className="text-left p-2 text-xs uppercase tracking-wider text-slate-500 font-semibold w-[130px]">Action</th>
                          <th className="text-left p-2 text-xs uppercase tracking-wider text-slate-500 font-semibold w-[120px]">Deal</th>
                          <th className="text-left p-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">Details / Changes</th>
                          <th className="text-center p-2 text-xs uppercase tracking-wider text-slate-500 font-semibold w-[50px]">View</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activityLogs.map((log, i) => {
                          const hasChanges = log.previous_values || log.new_values;
                          const isExpanded = expandedLogId === log.id;
                          const prevKeys = log.previous_values ? Object.keys(log.previous_values) : [];
                          const newKeys = log.new_values ? Object.keys(log.new_values) : [];
                          const allFields = [...new Set([...prevKeys, ...newKeys])];

                          return (
                            <tr
                              key={log.id || i}
                              className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${hasChanges ? 'cursor-pointer' : ''}`}
                              onClick={() => hasChanges && setExpandedLogId(isExpanded ? null : log.id)}
                              data-testid={`activity-log-row-${i}`}
                            >
                              <td className="p-2 text-xs text-slate-500 whitespace-nowrap font-mono align-top">
                                {formatTimestamp(log.timestamp)}
                              </td>
                              <td className="p-2 text-xs font-medium align-top">
                                {log.user_name}
                              </td>
                              <td className="p-2 align-top">
                                <span className={`inline-block text-xs px-2 py-0.5 font-bold uppercase ${
                                  log.category === 'auth' ? 'bg-blue-100 text-blue-700' :
                                  log.category === 'deal' ? 'bg-green-100 text-green-700' :
                                  log.category === 'admin' ? 'bg-red-100 text-red-700' :
                                  log.category === 'stip' ? 'bg-amber-100 text-amber-700' :
                                  log.category === 'lifecycle' ? 'bg-purple-100 text-purple-700' :
                                  log.category === 'note' ? 'bg-teal-100 text-teal-700' :
                                  log.category === 'interview' ? 'bg-indigo-100 text-indigo-700' :
                                  log.category === 'import' ? 'bg-cyan-100 text-cyan-700' :
                                  log.category === 'system' ? 'bg-gray-100 text-gray-600' :
                                  log.category === 'email' ? 'bg-pink-100 text-pink-700' :
                                  'bg-slate-100 text-slate-700'
                                }`}>
                                  {actionLabels[log.action] || log.action.replace(/_/g, ' ')}
                                </span>
                              </td>
                              <td className="p-2 text-xs align-top">
                                {log.deal_number ? (
                                  <span className="font-mono font-semibold text-slate-700">#{log.deal_number}</span>
                                ) : null}
                                {log.customer_name ? (
                                  <div className="text-slate-500 truncate max-w-[110px]" title={log.customer_name}>{log.customer_name}</div>
                                ) : null}
                              </td>
                              <td className="p-2 text-xs text-slate-600 align-top">
                                <div className="flex items-start gap-1">
                                  {hasChanges && (
                                    isExpanded 
                                      ? <ChevronUp size={14} className="text-slate-400 flex-shrink-0 mt-0.5" />
                                      : <ChevronDown size={14} className="text-slate-400 flex-shrink-0 mt-0.5" />
                                  )}
                                  <div className="min-w-0">
                                    {!isExpanded && (
                                      <span className="truncate block max-w-[400px]">{log.details}</span>
                                    )}
                                    {isExpanded && hasChanges && (
                                      <div className="space-y-1">
                                        <div className="text-xs text-slate-500 mb-1">{log.details}</div>
                                        <div className="bg-slate-50 border border-slate-200 p-2 text-xs space-y-1">
                                          {allFields.map(field => {
                                            const prev = log.previous_values?.[field];
                                            const next = log.new_values?.[field];
                                            const isAdd = prev === undefined || prev === null;
                                            const isRemove = next === undefined || next === null;
                                            return (
                                              <div key={field} className="flex items-center gap-1 flex-wrap">
                                                <span className="font-semibold text-slate-700">{formatFieldName(field)}:</span>
                                                {isAdd ? (
                                                  <span className="text-emerald-600">{renderChangeValue(next)}</span>
                                                ) : isRemove ? (
                                                  <span className="line-through text-red-500">{renderChangeValue(prev)}</span>
                                                ) : (
                                                  <>
                                                    <span className="text-red-500 line-through">{renderChangeValue(prev)}</span>
                                                    <ArrowRight size={12} className="text-slate-400 flex-shrink-0" />
                                                    <span className="text-emerald-600 font-medium">{renderChangeValue(next)}</span>
                                                  </>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                        {log.route && (
                                          <div className="text-[10px] text-slate-400 font-mono mt-1">{log.route}</div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="p-2 text-center align-top">
                                {log.deal_id && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); navigate(`/deal/${log.deal_id}`); }}
                                    className="text-blue-600 hover:text-blue-800 transition-colors"
                                    title="View Deal"
                                    data-testid={`activity-view-deal-${i}`}
                                  >
                                    <ExternalLink size={14} />
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="text-xs text-slate-400 mt-2">{activityLogs.length} entries shown (max 200)</p>
              </div>
            </TabsContent>
          )}
        </Tabs>

        {/* Save Button */}
        {isAdmin && (
          <div className="flex justify-end mt-6">
            <Button
              onClick={handleSaveSettings}
              disabled={saving}
              className="uppercase tracking-wide text-xs font-semibold"
              data-testid="save-settings-btn"
            >
              {saving ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} className="mr-2" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      <CSVUploadModal
        open={showCSVUpload}
        onClose={() => setShowCSVUpload(false)}
        onSuccess={() => {
          setShowCSVUpload(false);
          toast.success('CSV imported successfully! View deals in the Dashboard.');
        }}
      />
    </Layout>
  );
};
